import { EyeOutlined, PictureOutlined } from '@ant-design/icons';
import { Image, Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import authService from '../api-authorization/AuthorizeService';

export interface AuthenticatedAttachmentImageProps {
    attachmentId: number;
    alt?: string;
    width?: number;
    height?: number;
    className?: string;
    preview?: boolean;
}

/** Grey tile in place of a picture: no image yet, or one that could not be loaded. */
export const ImagePlaceholder: React.FC<{ width: number; height: number }> = ({
    width,
    height,
}) => (
    <div
        style={{
            width,
            height,
            borderRadius: 8,
            background: '#f5f5f5',
            color: '#bfbfbf',
            fontSize: Math.round(Math.min(width, height) / 3),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        <PictureOutlined />
    </div>
);

/** Fetches an attachment endpoint with the Bearer token (an img src cannot send auth) as a blob URL. */
const fetchBlobUrl = async (url: string): Promise<string | undefined> => {
    const token = await authService.getAccessToken();
    const res = await fetch(url, { headers: { authorization: `Bearer ${token}` } });

    if (!res.ok) return undefined;

    return URL.createObjectURL(await res.blob());
};

/**
 * Shows the server-made thumbnail (a few KB) instead of the original photo (often several MB), so
 * lists and galleries stay fast on site connections. The original is fetched only when the user
 * opens the preview.
 */
const AuthenticatedAttachmentImage: React.FC<AuthenticatedAttachmentImageProps> = ({
    attachmentId,
    alt,
    width = 56,
    height = 56,
    className,
    preview = true,
}) => {
    const [src, setSrc] = useState<string | undefined>();
    const [fullSrc, setFullSrc] = useState<string | undefined>();
    const [previewVisible, setPreviewVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const urlsRef = useRef<string[]>([]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setSrc(undefined);
        setFullSrc(undefined);

        (async () => {
            try {
                const u = await fetchBlobUrl(
                    `${window.location.origin}/api/Attachments/${attachmentId}/Thumbnail`
                );
                if (cancelled || !u) return;
                urlsRef.current.push(u);
                setSrc(u);
            } catch {
                if (!cancelled) setSrc(undefined);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
            urlsRef.current = [];
        };
    }, [attachmentId]);

    const openPreview = async (visible: boolean) => {
        setPreviewVisible(visible);

        if (!visible || fullSrc) return;

        try {
            const u = await fetchBlobUrl(
                `${window.location.origin}/api/Attachments/${attachmentId}/Download`
            );
            if (!u) return;
            urlsRef.current.push(u);
            setFullSrc(u);
        } catch {
            // Keep showing the thumbnail in the preview.
        }
    };

    if (loading) {
        return <Spin size="small" />;
    }

    // A missing or unreadable file keeps the cell's size instead of collapsing to nothing.
    if (!src) {
        return <ImagePlaceholder width={width} height={height} />;
    }

    return (
        <Image
            src={src}
            alt={alt || ''}
            width={width}
            height={height}
            className={className}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            preview={
                preview
                    ? {
                          visible: previewVisible,
                          // antd's "Pregled" label is cut to "Pr…" on small tiles; the icon says it.
                          mask: width < 100 ? <EyeOutlined /> : undefined,
                          src: fullSrc || src,
                          onVisibleChange: (visible) => void openPreview(visible),
                      }
                    : false
            }
        />
    );
};

export default AuthenticatedAttachmentImage;
